import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type {
  ManualPlacementConfig,
  MetaAdDraftInput,
  PublisherPlatform,
} from '../../../types/metaAdDraft';
import {
  PLATFORMS,
  hasAnyStoryPosition,
  placementCounts,
  positionArrayKey,
  positionsForPlatform,
} from '../../../lib/placementMatrix';
import { RadioGroup } from '../components/RadioGroup';
import { setPlacementMode, togglePlatform, togglePosition } from '../placements';

// Stage 7A D3.1 — full placement matrix UI. Four collapsible platform groups
// (Facebook / Instagram default-expanded; Messenger / Audience Network collapsed).
// Each position is a checkbox tagged with its asset class (Feed / Story); the group
// header carries a select-all/clear checkbox. Auto-pair dependencies (e.g. IG
// Explore Home → Explore) surface a transient inline message.

function AssetBadge({ assetClass }: { assetClass: 'feed' | 'story' }) {
  const isStory = assetClass === 'story';
  return (
    <span
      className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        isStory ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
      }`}
    >
      {isStory ? 'Story' : 'Feed'}
    </span>
  );
}

function CheckRow({
  label,
  checked,
  onChange,
  children,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-kyck-600 focus:ring-kyck-500"
      />
      <span className="flex items-center">
        {label}
        {children}
      </span>
    </label>
  );
}

function PlatformGroup({
  platform,
  label,
  note,
  defaultExpanded,
  cfg,
  onTogglePlatform,
  onTogglePosition,
}: {
  platform: PublisherPlatform;
  label: string;
  note?: string;
  defaultExpanded: boolean;
  cfg: ManualPlacementConfig;
  onTogglePlatform: (on: boolean) => void;
  onTogglePosition: (value: string, on: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const positions = positionsForPlatform(platform);
  const selected = cfg[positionArrayKey(platform)] as string[];
  const allOn = positions.length > 0 && positions.every((p) => selected.includes(p.value));

  return (
    <fieldset aria-label={label} className="rounded-md border border-slate-200">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-700"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {label}
          {note && <span className="ml-1 text-xs font-normal text-slate-400">{note}</span>}
          {selected.length > 0 && (
            <span className="ml-1.5 rounded-full bg-kyck-100 px-2 py-0.5 text-xs font-medium text-kyck-700">
              {selected.length}
            </span>
          )}
        </button>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={allOn}
            onChange={(e) => onTogglePlatform(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-kyck-600 focus:ring-kyck-500"
          />
          {allOn ? 'Clear all' : 'Select all'}
        </label>
      </div>

      {expanded && (
        <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-100 px-3 py-3">
          {positions.map((p) => (
            <CheckRow
              key={p.value}
              label={p.label}
              checked={selected.includes(p.value)}
              onChange={(on) => onTogglePosition(p.value, on)}
            >
              <AssetBadge assetClass={p.assetClass} />
            </CheckRow>
          ))}
        </div>
      )}
    </fieldset>
  );
}

export function PlacementsSection({
  value,
  onChange,
}: {
  value: MetaAdDraftInput;
  onChange: (patch: Partial<MetaAdDraftInput>) => void;
}) {
  const [open, setOpen] = useState(true);
  const [autoPairMessage, setAutoPairMessage] = useState<string | undefined>(undefined);
  // Multi-ad-set: placements are campaign-wide-locked (shared across all ad sets).
  const placements = value.campaign.placements;
  const isManual = placements.mode === 'manual';
  const cfg = placements.manualConfig;
  const hasStoryImage = Boolean(value.ads.creative.storyAsset.url);

  const setMode = (mode: 'automatic' | 'manual') => {
    setAutoPairMessage(undefined);
    onChange({ campaign: { ...value.campaign, placements: setPlacementMode(mode) } });
  };

  const setManual = (next: ManualPlacementConfig) =>
    onChange({ campaign: { ...value.campaign, placements: { mode: 'manual', manualConfig: next } } });

  const onTogglePosition = (platform: PublisherPlatform, posValue: string, on: boolean) => {
    const { cfg: next, message } = togglePosition(cfg!, platform, posValue, on);
    setAutoPairMessage(message);
    setManual(next);
  };

  const counts = cfg ? placementCounts(cfg) : { total: 0, feed: 0, story: 0 };
  const storyWithoutImage = cfg !== null && hasAnyStoryPosition(cfg) && !hasStoryImage;

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-slate-500"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Placements
      </button>

      {open && (
        <div className="space-y-4">
          <RadioGroup
            label="Placement mode"
            value={placements.mode}
            options={[
              { value: 'automatic', label: 'Automatic', description: 'Recommended' },
              { value: 'manual', label: 'Manual' },
            ]}
            onChange={setMode}
          />
          <p className="text-xs text-slate-400">
            Automatic placements are recommended for most campaigns. Use manual placements when you need to
            optimise for specific placements (e.g. Stories-only campaigns).
          </p>

          {!isManual && (
            <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
              Meta will optimise delivery across all eligible Facebook, Instagram, Messenger, and Audience
              Network placements based on best performance.
            </p>
          )}

          {isManual && cfg && (
            <div className="space-y-3">
              {PLATFORMS.map((pf) => (
                <PlatformGroup
                  key={pf.value}
                  platform={pf.value}
                  label={pf.label}
                  note={pf.note}
                  defaultExpanded={pf.defaultExpanded}
                  cfg={cfg}
                  onTogglePlatform={(on) => {
                    setAutoPairMessage(undefined);
                    setManual(togglePlatform(cfg, pf.value, on));
                  }}
                  onTogglePosition={(posValue, on) => onTogglePosition(pf.value, posValue, on)}
                />
              ))}

              {autoPairMessage && <p className="text-xs text-slate-500">{autoPairMessage}</p>}

              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>
                  {counts.total} placement{counts.total === 1 ? '' : 's'} selected
                  {counts.total > 0 && ` · ${counts.feed} Feed · ${counts.story} Story`}
                </span>
              </div>

              {counts.total === 0 && (
                <p className="text-xs text-amber-600">
                  Choose at least one placement, or switch back to Automatic.
                </p>
              )}

              {storyWithoutImage && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  You've selected Story placements but haven't added a Story image (9:16). Add one in the Ad
                  section, or those placements will be dropped at publish.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
